import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiUser, FiHash, FiCalendar, FiFileText } from 'react-icons/fi';
import AdminShell from '../../components/AdminShell/AdminShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import AdminWalletBadge from '../../components/AdminWalletBadge/AdminWalletBadge';
import Drawer from '../../components/Drawer/Drawer';
import drawerStyles from '../../components/Drawer/Drawer.module.css';
import Pagination from '../../components/Pagination/Pagination';
import styles from '../../styles/adminPanels.module.css';
import axiosClient from '../../api/axiosClient';

interface KycDoc {
  id: string;
  account_name: string;
  account_email: string;
  full_name: string;
  id_number: string;
  dob: string | null;
  document_category: string;
  document_type: string;
  front_file_path: string;
  back_file_path: string | null;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

const STATUS_COLORS: Record<string, string> = { pending: '#e8a86a', approved: '#5cb87d', rejected: '#e2534a' };
const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');

const AdminKyc: React.FC = () => {
  const [documents, setDocuments] = useState<KycDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<KycDoc | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 8;

  const load = () => {
    setLoading(true);
    axiosClient.get('/admin/kyc').then(({ data }) => setDocuments(data.documents)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id: string, status: string) => {
    try {
      await axiosClient.patch(`/admin/kyc/documents/${id}/status`, { status });
      toast.success(`Document ${status}.`);
      setSelected(null);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not update status.');
    }
  };

  return (
    <AdminShell>
      <PageHeader title="KYC review" subtitle="Identity, address and income documents submitted by individual investors. Click a row for full details." action={<AdminWalletBadge />} />
      <div className={styles.page}>
        <div className={styles.tableWrap}>
          {loading && <div className={styles.loading}><span className={styles.spinnerLg} /></div>}
          {!loading && documents.length === 0 && <div className={styles.empty}>No KYC submissions yet.</div>}
          {!loading && documents.length > 0 && (
            <table className={styles.table}>
              <thead>
                <tr><th>User</th><th>ID number</th><th>Document</th><th>Status</th><th>Submitted</th></tr>
              </thead>
              <tbody>
                {documents.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((d) => (
                  <tr key={d.id} onClick={() => setSelected(d)} style={{ cursor: 'pointer' }}>
                    <td><div className={styles.nameCell}>{d.full_name || d.account_name}</div><div className={styles.subCell}>{d.account_email}</div></td>
                    <td>{d.id_number || '—'}</td>
                    <td>
                      <div className={styles.nameCell} style={{ textTransform: 'capitalize' }}>{d.document_category}</div>
                      <div className={styles.subCell}>{d.document_type}</div>
                    </td>
                    <td><span className={styles.statusPill} style={{ color: STATUS_COLORS[d.status], borderColor: STATUS_COLORS[d.status] }}>{d.status}</span></td>
                    <td className={styles.subCell}>{new Date(d.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!loading && documents.length > 0 && (
            <Pagination page={page} totalPages={Math.max(1, Math.ceil(documents.length / PAGE_SIZE))} onChange={setPage} />
          )}
        </div>
      </div>

      <Drawer open={!!selected} onClose={() => setSelected(null)} title="KYC submission">
        {selected && (
          <>
            <div className={drawerStyles.detailRow}>
              <div className={drawerStyles.detailLabel}><FiUser style={{ marginRight: 6 }} />Account</div>
              <div className={drawerStyles.detailValue}>{selected.account_name}</div>
              <div className={styles.subCell}>{selected.account_email}</div>
            </div>

            <div className={drawerStyles.detailGrid}>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Full name on document</div>
                <div className={drawerStyles.detailValue}>{selected.full_name || '—'}</div>
              </div>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}><FiHash style={{ marginRight: 4 }} />ID number</div>
                <div className={drawerStyles.detailValue}>{selected.id_number || '—'}</div>
              </div>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}><FiCalendar style={{ marginRight: 4 }} />Date of birth</div>
                <div className={drawerStyles.detailValue}>{selected.dob ? new Date(selected.dob).toLocaleDateString() : '—'}</div>
              </div>
              <div className={drawerStyles.detailRow}>
                <div className={drawerStyles.detailLabel}>Submitted</div>
                <div className={drawerStyles.detailValue}>{new Date(selected.created_at).toLocaleString()}</div>
              </div>
            </div>

            <div className={drawerStyles.detailRow}>
              <div className={drawerStyles.detailLabel}><FiFileText style={{ marginRight: 4 }} />Document type</div>
              <div className={drawerStyles.detailValue} style={{ textTransform: 'capitalize' }}>{selected.document_category} — {selected.document_type}</div>
            </div>

            <div className={drawerStyles.detailRow}>
              <div className={drawerStyles.detailLabel}>Files</div>
              <div>
                <a className={drawerStyles.fileLink} href={`${API_ORIGIN}${selected.front_file_path}`} target="_blank" rel="noreferrer">Front / Document</a>
                {selected.back_file_path && (
                  <a className={drawerStyles.fileLink} href={`${API_ORIGIN}${selected.back_file_path}`} target="_blank" rel="noreferrer">Back</a>
                )}
              </div>
            </div>

            <div className={drawerStyles.detailRow}>
              <div className={drawerStyles.detailLabel}>Current status</div>
              <span className={styles.statusPill} style={{ color: STATUS_COLORS[selected.status], borderColor: STATUS_COLORS[selected.status] }}>{selected.status}</span>
            </div>
          </>
        )}
        {selected && (
          <div className={drawerStyles.footer} style={{ position: 'sticky', bottom: 0, background: '#0d1420', margin: '24px -24px -24px' }}>
            {selected.status !== 'approved' && (
              <button className={styles.actionBtn} onClick={() => updateStatus(selected.id, 'approved')}>Approve</button>
            )}
            {selected.status !== 'rejected' && (
              <button className={styles.actionBtnReject} onClick={() => updateStatus(selected.id, 'rejected')}>Reject</button>
            )}
          </div>
        )}
      </Drawer>
    </AdminShell>
  );
};

export default AdminKyc;
